import React, { useState, useEffect } from 'react';
import { Modal, Image, Card, Form, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCrown, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import Markdown from 'markdown-to-jsx';
import Logo from '../../beatai-logo.svg';

function ModelCapabilities({ show, handleClose, model, setModel, modelChangeToast, changeModel }) {
  const [current_plan, setCurrentPlan] = useState(localStorage.getItem("current_plan"));
  
  useEffect(() => {
    setCurrentPlan(localStorage.getItem("current_plan"));
  }, []);

  const models = [
    {
      id: 'deepseekr1',
      name: 'DeepSeek R1',
      description: 'Model with advanced reasoning output',
      url: 'https://www.deepseek.com/',
      isBeta: false,
      isPremium: false
    },
    {
      id: 'openai',
      name: 'OpenAI GPT-4o',
      description: 'Advanced language model with multimodal capabilities',
      url: 'https://chatgpt.com/',
      isBeta: false,
      isPremium: false
    },
    {
      id: 'gemini',
      name: 'Gemini 2.0 Flash',
      description: 'Fast and efficient for quick responses',
      url: 'https://ai.google.dev/pricing#2_0flash',
      isBeta: false,
      isPremium: false
    },
    {
      id: 'xai',
      name: 'xAI Grok - Beta',
      description: 'Problem-solving focused with analytical capabilities',
      url: 'https://grok.com/?referrer=website',
      isBeta: false,
      isPremium: true
    },
    {
      id: 'claude',
      name: 'Claude 3.7 Sonnet',
      description: 'Specialized in technical and analytical tasks',
      url: 'https://claude.ai/',
      isBeta: false,
      isPremium: true
    },
    {
      id: 'qwenvmax',
      name: 'Qwen-VL-Max',
      description: 'Model with advanced text, image and video analysis.',
      url: 'https://qwenlm.github.io/',
      isBeta: true,
      isPremium: false
    },
  ];

  const handleModelChange = (modelId) => {
    const selectedModel = models.find(m => m.id === modelId);
    
    if (selectedModel.isPremium && !current_plan) {
      // Redirect to membership page for premium models if no paid plan
      window.location.href = "/membership";
      return;
    }
    
    localStorage.setItem("model", modelId);
    setModel(modelId);
    const messages = {
      openai: "You're now using OpenAI's GPT-4o model.",
      deepseekr1: "You're now using DeepSeek's Rl with reasoning capabilities. Hosted in US Data Centers.",
      gemini: "You're now using Google's Gemini 2.0 Flash.",
      xai: "You're now using xAI's Grok Model.",
      qwenvmax: "You're now using Qwen VL Max - Alibaba's Latest Qwen Model. Hosted on Alibaba Data Centers.",
      claude: "You're now using Anthropic's Claude 3.7 Sonnet."
    };
    modelChangeToast(messages[modelId]);
  };

  const markd = `

DeepSeek AI (Reasoning AI Model)<br />
*What Can It Do For You?*
- Designed for natural language processing and text generation.
- Excels in reasoning and logical inference tasks, making it suitable for complex problem-solving.
- *Perfect for*: Content creation, data analysis, customer engagement
<br/>[<span style="font-size: 0.8em; color: #d9d9d9; text-decoration: none; pointer-events: none;">TogetherAI Privacy Policy</span>](https://www.together.ai/privacy)

---

GPT-4o (OpenAI)  
*What Can It Do For You?*  
- Functions like a human brain—sees, hears, and talks simultaneously.  
- Responds quickly, like a human in a conversation.  
- Detects emotions in voice and text.  
- Ideal for natural, back-and-forth conversations.  
- Adapts to your tone—professional or casual.  
- *Perfect for:* Customer service, content creation, and personal assistance.  
[<span style="font-size: 0.8em; color: #d9d9d9; text-decoration: none; pointer-events: none;">OpenAI Privacy Policy</span>](https://platform.openai.com/docs/models/how-we-use-your-data#how-we-use-your-data)

---

Gemini 2.0 Flash (Google)  
*What Can It Do For You?*  
- Lightning-fast responses, like a speedy assistant.  
- Multitasks seamlessly without slowing down.  
- Analyzes pictures and videos with clarity.  
- Summarizes lengthy content into key points.  
- *Perfect for:* Quick answers, multitasking, and content summaries.  
[<span style="font-size: 0.8em; color: #d9d9d9; text-decoration: none; pointer-events: none;">Google AI Privacy Policy</span>](https://cloud.google.com/vertex-ai/generative-ai/docs/data-governance)

---

Grok Beta (xAI)  
*What Can It Do For You?*  
- Solves tough problems step by step.  
- Understands casual language with precision.  
- Detects patterns and suggests solutions.  
- Helps debug and optimize code.  
- Simplifies charts and documents.  
- *Perfect for:* Problem-solving, coding help, and analytical tasks.  
[<span style="font-size: 0.8em; color: #d9d9d9; text-decoration: none; pointer-events: none;">xAI Privacy Policy</span>](https://x.ai/legal/privacy-policy)

---

Claude 3.7 Sonnet (Anthropic)  
*What Can It Do For You?*  
- Excels at explaining charts and data visualizations.  
- Writes and debugs code accurately.  
- Works significantly faster than older versions.  
- Tackles expert-level, complex topics.  
- Understands jokes and nuanced context.  
- *Perfect for:* Data analysis, coding projects, and academic work.  
[<span style="font-size: 0.8em; color: #d9d9d9; text-decoration: none; pointer-events: none;">Anthropic Privacy Policy</span>](https://www.anthropic.com/legal/privacy)

---

Simple Guide: Which One to Use?  

| *If You Need To...*              | *Use This*             |  
|------------------------------------|--------------------------|
| Obtain Reasoning                   | DeepSeek R1              |
| Have natural conversations         | GPT-4o                   |  
| Get quick answers                  | Gemini 2.0 Flash         |  
| Solve tricky problems              | Grok Beta                |  
| Work with data or code             | Claude 3.7 Sonnet        |  
| Handle pictures and text together  | GPT-4o or Gemini 2.0 Flash |  
| Analyze complex information        | Claude 3.7 Sonnet        |  

---

Think of it like this:  
- *DeepSeek R1*: Reasoning AI Model.  
- *GPT-4o*: Your all-around smart friend.  
- *Gemini 2.0 Flash*: Your quick-thinking assistant.  
- *Grok Beta*: Your problem-solving buddy.  
- *Claude 3.7 Sonnet*: Your technical expert.  

`;

  return (
    <Modal
      size="lg"
      className="modal-model-capabilities"
      show={show}
      onHide={handleClose}
      animation={false}
    >
      <Modal.Header closeButton>
        <Modal.Title
          style={{
            fontSize: "0.9em",
            textAlign: "center",
            width: "100%"
          }}
        >
          <Image
            style={{
              maxWidth: "100px",
              margin: "0 auto",
            }}
            src={Logo}
          /> Select Model for <span className="lex-color" style={{
            fontSize: "1.1em"
          }}>Lex</span> to use
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Model Selection Cards */}
        <div className="model-cards mb-4">
          <div className="row g-3">
            {models.map((modelOption) => (
              <div className="col-md-4" key={modelOption.id}>
                <Card 
                  className='modal-cap'
                  style={{
                    color: "#d9d9d9",
                    background: 'rgba(163, 149, 196, 0.04)',
                    backdropFilter: 'blur(4px)',
                    border: model === modelOption.id ? '2px solid rgb(147, 51, 234)' : '1px solid rgba(147, 51, 234, 0.3)',
                    position: "relative",
                    fontSize: "0.9em",
                    opacity: (modelOption.isPremium && !current_plan) ? 0.7 : 1,
                    cursor: (modelOption.isPremium && !current_plan) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <Card.Body className="p-3 position-relative">
                    <Form.Check
                      type="radio"
                      id={`model-${modelOption.id}`}
                      name="model"
                      checked={model === modelOption.id}
                      onChange={() => handleModelChange(modelOption.id)}
                      label={
                        <div className="ms-2">
                          <div className="d-flex align-items-center">
                            <img
                              height="16"
                              style={{marginRight: "4px"}}
                              width="16"
                              src={`http://www.google.com/s2/favicons?domain=${modelOption.url}`}
                            />{" "}
                            {modelOption.name}
                            {model === modelOption.id && (
                              <FontAwesomeIcon icon={faCheckCircle} className="ms-2 text-purple-600" />
                            )}
                          </div>
                        </div>
                      }
                    />
                    {modelOption.isBeta && (
                      <Badge className='badge-beta' bg="purple" style={{
                        position: "absolute",
                        top: 1,
                        right: 8,
                        transform: "translateY(-50%)",
                        color: "white",
                        fontSize: "0.65em",
                      }}>
                        Beta
                      </Badge>
                    )}
                    {modelOption.isPremium && (
                      <Badge bg="secondary" style={{
                        position: "absolute",
                        top: 1,
                        fontWeight: "100",
                        right: modelOption.isBeta ? 50 : 8,
                        transform: "translateY(-50%)",
                        color: "white",
                        fontSize: "0.5em",
                        display: "flex",
                        alignItems: "center",
                      }}>
                        <FontAwesomeIcon 
                          icon={faCrown} 
                          style={{ 
                            marginRight: "3px", 
                            fontSize: "0.8em" 
                          }} 
                        />
                        Premium
                      </Badge>
                    )}
                    {modelOption.isPremium && !current_plan && (
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "rgba(0, 0, 0, 0.15)",
                          borderRadius: "5px",
                          zIndex: 2,
                        }}
                      >
                        <div style={{
                          background: "rgba(160, 32, 240, 0.2)", // Matching the premium purple color
                          borderRadius: "4px",
                          fontWeight: "500",
                          fontSize: "0.75em",
                          backdropFilter: "blur(2px)",
                          WebkitBackdropFilter: "blur(2px)",
                          border: "1px solid rgba(160, 32, 240, 0.3)",
                          color: "#d9d9d9"
                        }}>
                          
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </div>
            ))}
          </div>
        </div>

        {/* Plan Status Message */}
        {!current_plan && (
          <div 
            className="mb-4" 
            style={{
              background: "rgba(255, 255, 255, 0.07)",
              borderRadius: "8px",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div style={{ 
              color: "white", 
              fontSize: "0.85em",
              display: "flex",
              alignItems: "center"
            }}>
              <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: "8px", color: "#a020f0" }} />
              You're using the free plan. Upgrade to use premium models.
            </div>
            <div 
              onClick={() => window.location.href = "/membership"}
              style={{
                background: "linear-gradient(135deg, #8e24aa, #673ab7)",
                border: "none",
                boxShadow: "0 2px 8px rgba(142, 36, 170, 0.3)",
                padding: "5px 12px",
                borderRadius: "6px",
                fontSize: "0.8em",
                transition: "all 0.3s ease",
                color: "white",
                cursor: "pointer",
                fontWeight: "500",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 4px 10px rgba(142, 36, 170, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(142, 36, 170, 0.3)";
              }}
            >
              Upgrade
            </div>
          </div>
        )}

        <hr className="my-4" />

        {/* Original Markdown Content */}
        <Markdown style={{
          fontSize: "0.8em"
        }}>{markd}</Markdown>

        <style jsx>{`
          .model-cards .card:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(147, 51, 234, 0.2);
          }
        `}</style>
      </Modal.Body>
    </Modal>
  );
}

export default ModelCapabilities;